import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import PersonIcon from '@material-ui/icons/Person';
import { MailRounded } from '@material-ui/icons';
import LockIcon from '@material-ui/icons/Lock';
import InputAdornment from '@material-ui/core/InputAdornment';
import { Box } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { useDispatch, useSelector } from 'react-redux';
import validator from 'validator';
import ContentWrapper from '/components/Utilities/ContentWrapper';
import CustomContainer from '/components/Utilities/CustomContainer';
import LoginBackgroundImage from '/assets/conncetRn-logo/crn_rebranding_bg_img.svg';
import ConnectRNLightLogo from '/assets/conncetRn-logo/crn_rebranding_new_logo_light.svg';
import Image from '/components/Utilities/Image';
import {
	setLoginEmail,
	setLoginPassword,
	login,
	setLoginMessage,
	setNewUser,
	setUserID,
	setUpdatePass
} from '/redux/login';
import Agent from '/api/Agent';
import './Login.css';
import { IncorrectPasswordAlert } from './IncorrectPasswordAlert';
import { NoPermissionAlert } from './NoPermissionAlert';
import { RoutePathEnum } from 'src/constants/route-path-enum';

export const testIDs = {
	emailInput: 'login-email-input',
	loginSignInBtn: 'login-sign-in-btn',
	passwordInput: 'login-password-input'
};

const Login = () => {
	const history = useHistory();
	const location = useLocation();
	// state :  existing, new , reset, forgot
	const [state, setState] = useState('existing');
	const [email, setEmail] = useState('');
	const [pass, setPass] = useState('');
	const [passVisible, setPassVisible] = useState(false);
	const [passConfirm, setPassConfirm] = useState('');
	const [confirmPassVisible, setConfirmPassVisible] = useState(false);
	const [err, setErr] = useState('');
	const [tempPassword, setTempPassword] = useState('');
	const { hash, search } = useLocation();
	const dispatch = useDispatch();
	const message = useSelector((status) => status.login.message);
	const newUser = useSelector((status) => status.login.newUser);
	const updatePass = useSelector((status) => status.login.updatePassword);
	const [id, setID] = useState('');
	const userID = useSelector((status) => status.login.userId);
	const [alertType, setAlertType] = useState('error');
	const [passwordMatch, setPasswordMatch] = useState(true);
	const mimicToken = useSelector((status) => status.login.mimicToken);
	const params = new URLSearchParams(search);
	const failureReason = params.get('failure_reason');

	const isQRCheckIn =
		params.has('redirectTo') && params.get('redirectTo').startsWith(RoutePathEnum.SUPERVISOR_QR_CHECK_IN);
	const isQRCheckOut =
		params.has('redirectTo') && params.get('redirectTo').startsWith(RoutePathEnum.SUPERVISOR_QR_CHECKOUT);

	useEffect(() => {
		if (hash) {
			const final = hash.split('/');
			if (final[1]) {
				setID(final[1]);
				setState('reset');
			}
		} else if (newUser) {
			setTempPassword(pass);
			setState('new');
		} else if (updatePass) {
			setTempPassword(pass);
			setState('reset');
		}
		dispatch(setLoginMessage(''));
	}, [newUser, updatePass]);

	const handlePasswordValidation = async (password) => {
		if (password.length < 8) {
			setErr('Your password must be a minimum of 8 characters long.');
			return;
		}
		if (password.search(/\d/) === -1) {
			setErr('Your password must contain at least 1 number.');
			return;
		}
		if (password.search(/[A-Z]/) === -1) {
			setErr('Your password must contain at least 1 uppercase character.');
			return;
		}
		if (password.search(/[a-z]/) === -1) {
			setErr('Your password must contain at least 1 lowercase character.');
			return;
		}
		if (password.search(/[-!$%^&*()_+|~=`{}[:;<>?,.@#\]]/g) === -1) {
			setErr('Your password must contain at least 1 special character.');
			return;
		}
		if (pass !== passConfirm) {
			setPasswordMatch(false);
			return;
		}
		return true;
	};

	const onLogin = async (mail, password) => {
		dispatch(setLoginEmail(mail));
		dispatch(setLoginPassword(password));

		try {
			const response = await dispatch(login());

			if (response.ok) {
				const [, path, search] = location.search.split('?');
				const params = new URLSearchParams(path);
				if (params.has('redirectTo')) {
					const queryString = search ? `?${search}` : '';
					const newURL = `${params.get('redirectTo')}${queryString}`;
					history.push(newURL);
				}
			}
		} catch (err) {
			// Error handling happens via redux and redux state
		}
	};

	const onUpdatePassword = (password) => {
		if (userID !== 0 || id) {
			Agent.newUpdatePassword(userID, password, mimicToken ? '' : tempPassword, id, mimicToken).then((res) => {
				if (res.ok) {
					dispatch(setLoginMessage('Your password has been updated!'));
					setAlertType('success');
					dispatch(setNewUser(false));
					dispatch(setUpdatePass(false));
					dispatch(setUserID(0));
					dispatch(setState('existing'));
				} else {
					dispatch(setLoginMessage(res.data));
					setAlertType('error');
				}
			});
		} else {
			dispatch(setLoginMessage('Something went wrong'));
		}
	};

	const onForgotPassword = (mail) => {
		Agent.forgotPassword(mail).then((response) => {
			if (response.ok) {
				dispatch(
					setLoginMessage('An email with instructions on how to reset your password has been successfully sent.')
				);
				setAlertType('success');
			} else {
				if (response.data !== null && response.data.trim() === 'Email address not found.') {
					dispatch(
						setLoginMessage('An email with instructions on how to reset your password has been successfully sent.')
					);
					setAlertType('success');
					return;
				}
				dispatch(
					setLoginMessage(`Failed to find account: ${response.data === null ? 'something went wrong' : response.data}`)
				);
				setAlertType('error');
			}
		});
	};

	return (
		<ContentWrapper
			backgroundColor="#001936"
			backgroundImage={LoginBackgroundImage}
			backgroundImagePosition="0% 100%"
			backgroundSize="auto"
			position="fixed"
			top="0"
		>
			<CustomContainer className="CustomContainer-login">
				<Grid container item justifyContent="center" xs={12}>
					<Grid container item justifyContent="center" xs={12}>
						<CustomContainer className="CustomContainer-loginLogo">
							<Image src={ConnectRNLightLogo} />
						</CustomContainer>
					</Grid>
					<Grid container item justifyContent="center" xs={12}>
						<CustomContainer className="CustomContainer-mainContainer">
							{failureReason === 'no_shift_access' ? (
								<NoPermissionAlert message={message} />
							) : (
								message !== '' && (
									<CustomContainer className="CustomContainer-alertContainer">
										<IncorrectPasswordAlert alertType={alertType} message={message} setLoginMessage={setLoginMessage} />
									</CustomContainer>
								)
							)}
							<Grid alignItems="center" container item xs={12}>
								<Grid item xs={12}>
									<Grid container item xs={12}>
										<Box mb={3}>
											<CustomContainer className="CustomContainer-header">
												{state === 'existing' ? (
													<h2>{isQRCheckIn || isQRCheckOut ? 'Supervisor Login' : 'Welcome to connectRN'}</h2>
												) : state === 'new' ? (
													<>
														<h2>Let&apos;s Get Started</h2>
														<p>
															Your password must contain at least one uppercase and a lowercase letter, a number, a
															special character and be at least eight characters long
														</p>
													</>
												) : state === 'forgot' ? (
													<>
														<h2>Forgot Password?</h2>
														<p>To reset your password, please enter the email address associated with your account.</p>
													</>
												) : state === 'reset' && id === '' ? (
													<>
														<h2>Oops, your password expired!</h2>
														<p>
															Your password must contain at least one uppercase and a lowercase letter, a number, a
															special character and be at least eight characters long
														</p>
													</>
												) : state === 'reset' && id !== '' ? (
													<>
														<h2>Reset Password</h2>
														<p>To update your password, please enter and confirm a new password below.</p>
														<p>
															Your new password must be 8 characters long and contain at least one uppercase and one
															lowercase letter, one number, and one special character.
														</p>
													</>
												) : (
													<h2>Let&apos;s Get Started</h2>
												)}
												<p className="CustomErrorMsg">{err}</p>
											</CustomContainer>
										</Box>
									</Grid>
									<Grid container item xs={12}>
										<Grid alignItems="flex-end" container>
											{state !== 'reset' && state !== 'new' && (
												<Grid item xs={12}>
													<Box mb={state !== 'forgot' ? 3 : 0}>
														<TextField
															InputProps={{
																startAdornment: (
																	<InputAdornment position="start">
																		{(state === 'existing' && <PersonIcon />) ||
																			(state === 'forgot' && <MailRounded />)}
																	</InputAdornment>
																)
															}}
															data-cy={testIDs.emailInput}
															fullWidth
															id="email"
															onChange={(e) => {
																setEmail(e.target.value);
																setErr('');
															}}
															placeholder="Email Address"
															value={email}
														/>
													</Box>
												</Grid>
											)}
											{state !== 'forgot' && (
												<Grid item xs={12}>
													<TextField
														InputProps={{
															startAdornment: (
																<InputAdornment position="start">
																	<LockIcon />
																</InputAdornment>
															)
														}}
														data-cy={testIDs.passwordInput}
														fullWidth
														id="password"
														onChange={(e) => {
															setPass(e.target.value);
															setErr('');
														}}
														placeholder={state === 'reset' ? 'New password' : 'Password'}
														type={passVisible ? 'text' : 'password'}
														value={pass}
													/>
													{pass.length > 0 && (
														<button
															className="ShowHidePassword"
															onClick={() => setPassVisible(!passVisible)}
															type="button"
														>
															{passVisible ? 'Hide' : 'Show'}
														</button>
													)}
												</Grid>
											)}
											{(state === 'reset' || state === 'new') && (
												<Grid xs={12}>
													<Box mt={3}>
														<TextField
															InputProps={{
																startAdornment: (
																	<InputAdornment position="start">
																		<LockIcon />
																	</InputAdornment>
																)
															}}
															fullWidth
															helperText={!passwordMatch && 'Password does not match'}
															id="passwordConfirm"
															onChange={(e) => {
																setPassConfirm(e.target.value);
																setPasswordMatch(true);
															}}
															placeholder="Confirm new password"
															type={confirmPassVisible ? 'text' : 'password'}
															value={passConfirm}
														/>
														{passConfirm.length > 0 && (
															<button
																className="ShowHidePassword"
																onClick={() => setConfirmPassVisible(!confirmPassVisible)}
																type="button"
															>
																{confirmPassVisible ? 'Hide' : 'Show'}
															</button>
														)}
													</Box>
												</Grid>
											)}
											{state !== 'forgot' && state !== 'new' && state !== 'reset' && (
												<Grid container item justifyContent="flex-end" xs={12}>
													<Box mb={3} mt={1}>
														<CustomContainer className="CustomContainer-forgotPassBtn">
															<Button onClick={() => setState('forgot')} role="button" type="button">
																FORGOT YOUR PASSWORD?
															</Button>
														</CustomContainer>
													</Box>
												</Grid>
											)}
											<Grid container item xs={12}>
												<Grid item xs={12}>
													<Box mt={state !== 'forgot' && state !== 'new' && state !== 'reset' ? 0 : 3}>
														{state === 'existing' ? (
															<Button
																color="primary"
																data-cy={testIDs.loginSignInBtn}
																fullWidth
																onClick={() => onLogin(email, pass)}
																variant="contained"
															>
																SIGN IN
															</Button>
														) : (
															<>
																<Button
																	color="primary"
																	disabled={state === 'forgot' && !validator.isEmail(email)}
																	fullWidth
																	onClick={() => {
																		switch (state) {
																			case 'reset':
																			case 'new':
																				handlePasswordValidation(pass).then((res) => {
																					if (res) {
																						setErr('');
																						onUpdatePassword(pass);
																					}
																				});
																				break;
																			case 'forgot':
																				onForgotPassword(email);
																				break;

																			default:
																				break;
																		}
																	}}
																	variant="contained"
																>
																	{state === 'forgot' ? 'RESET PASSWORD' : 'UPDATE PASSWORD'}
																</Button>
																<CustomContainer className="CustomContainer-forgotPassBtn">
																	<Grid container justifyContent="center">
																		<Button
																			onClick={() => {
																				setState('existing');
																				history.push('/', { params });
																				dispatch(setNewUser(false));
																				dispatch(setUpdatePass(false));
																				dispatch(setUserID(0));
																			}}
																			role="button"
																			type="button"
																		>
																			GO BACK TO LOGIN
																		</Button>
																	</Grid>
																</CustomContainer>
															</>
														)}
													</Box>
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
								{/* Right Side */}
								<Grid item xs={12}>
									<Box mt={2}>
										<Grid alignItems="center" container item justifyContent="center" xs={12}>
											<CustomContainer className="CustomContainer-secondaryContainer">
												{state === 'forgot' ? (
													<p>An email will be sent with instructions on the how to reset your password.</p>
												) : (
													!isQRCheckIn ||
													(!isQRCheckOut && (
														<>
															<h3>{state === 'existing' ? 'Trouble logging in?' : 'Questions?'}</h3>
															<p>
																Call us at <a href="tel:6179441515">(617) 944-1515</a>
															</p>
														</>
													))
												)}
											</CustomContainer>
										</Grid>
									</Box>
								</Grid>
							</Grid>
						</CustomContainer>
					</Grid>
				</Grid>
			</CustomContainer>
		</ContentWrapper>
	);
};

export default Login;
